import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { 
    SEASONAL_THEMES, 
    getActiveSeasonalTheme, 
    getEasterThemeRange, 
    getBlackFridayRange 
} from '../utils/seasonalThemes';
import '../styles/seasonal-themes.css';

const SeasonalThemeContext = createContext(null);

const STORAGE_KEYS = {
    ENABLED: 'enovadent_seasonal_enabled',
    DECORATIONS: 'enovadent_seasonal_decorations',
    OVERRIDE: 'enovadent_theme_override'
};

export const SeasonalThemeProvider = ({ children }) => {
    // 1. Initialiser depuis le localStorage avec des valeurs par défaut élégantes
    const [enabled, setEnabledState] = useState(() => {
        const saved = localStorage.getItem(STORAGE_KEYS.ENABLED);
        return saved !== null ? saved === 'true' : true;
    });

    const [decorationsEnabled, setDecorationsEnabledState] = useState(() => {
        const saved = localStorage.getItem(STORAGE_KEYS.DECORATIONS);
        return saved !== null ? saved === 'true' : true;
    });

    const [themeOverride, setThemeOverrideState] = useState(() => {
        return localStorage.getItem(STORAGE_KEYS.OVERRIDE) || null;
    });

    // 2. Détecter l'événement saisonnier naturel du jour
    const [today, setToday] = useState(new Date());

    // Vérifier l'heure/jour toutes les heures pour bascule fluide
    useEffect(() => {
        const interval = setInterval(() => {
            setToday(new Date());
        }, 60 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const naturalThemeId = useMemo(() => {
        return getActiveSeasonalTheme(today);
    }, [today]);

    // 3. Calculer le thème effectif
    const effectiveThemeId = useMemo(() => {
        if (!enabled) return 'default';
        if (themeOverride && SEASONAL_THEMES[themeOverride]) {
            return themeOverride;
        }
        return naturalThemeId;
    }, [enabled, themeOverride, naturalThemeId]);

    const themeMeta = useMemo(() => {
        return SEASONAL_THEMES[effectiveThemeId] || SEASONAL_THEMES.default;
    }, [effectiveThemeId]);

    // 4. Appliquer les attributs sur document.body et la balise racine HTML
    useEffect(() => {
        const root = document.documentElement;
        const body = document.body;

        if (effectiveThemeId && effectiveThemeId !== 'default') {
            root.setAttribute('data-seasonal-theme', effectiveThemeId);
            body.setAttribute('data-seasonal-theme', effectiveThemeId);
        } else {
            root.removeAttribute('data-seasonal-theme');
            body.removeAttribute('data-seasonal-theme');
        }
    }, [effectiveThemeId]);

    // 5. Actions d'administration et de personnalisation
    const setEnabled = (val) => {
        setEnabledState(val);
        localStorage.setItem(STORAGE_KEYS.ENABLED, String(val));
    };

    const setDecorationsEnabled = (val) => {
        setDecorationsEnabledState(val);
        localStorage.setItem(STORAGE_KEYS.DECORATIONS, String(val));
    };

    const setThemeOverride = (themeId) => {
        if (!themeId || themeId === 'auto' || themeId === naturalThemeId) {
            setThemeOverrideState(null);
            localStorage.removeItem(STORAGE_KEYS.OVERRIDE);
        } else {
            setThemeOverrideState(themeId);
            localStorage.setItem(STORAGE_KEYS.OVERRIDE, themeId);
        }
    };

    const resetToAuto = () => {
        setThemeOverrideState(null);
        localStorage.removeItem(STORAGE_KEYS.OVERRIDE);
    };

    const value = {
        enabled,
        setEnabled,
        decorationsEnabled,
        setDecorationsEnabled,
        themeOverride,
        setThemeOverride,
        resetToAuto,
        currentThemeId: effectiveThemeId,
        themeMeta,
        naturalThemeId,
        isSeasonalActive: effectiveThemeId !== 'default',
        isOverridden: themeOverride !== null,
        availableThemes: Object.values(SEASONAL_THEMES),
        getEasterThemeRange,
        getBlackFridayRange
    };

    return (
        <SeasonalThemeContext.Provider value={value}>
            {children}
        </SeasonalThemeContext.Provider>
    );
};

export const useSeasonalTheme = () => {
    const context = useContext(SeasonalThemeContext);
    if (!context) {
        throw new Error('useSeasonalTheme must be used within a SeasonalThemeProvider');
    }
    return context;
};
